import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslate } from '../hooks/useTranslate';
import { DataSource, Folder } from '../types';
import { getButtonClasses, getButtonStyles } from '../constants';
import { validateFolderName, FolderNameValidationResult, folderValidationMessage } from '../utils/validation/folderValidation';

interface MoveDataObjectModalProps {
    isOpen: boolean;
    dataObject?: DataSource;
    folders: Folder[];
    onClose: () => void;
    onMove: (targetFolderId: number | undefined, createdFolder?: Folder) => void;
    onCreateFolder?: (folderName: string) => Promise<FolderNameValidationResult & { folder?: Folder }>;
}

// Modal to move (or unassign) a data object to a different folder.
// Accessibility: role="dialog", aria-modal, labelled by title id, Escape closes (returns to actions modal via parent).
const MoveDataObjectModal: React.FC<MoveDataObjectModalProps> = ({
    isOpen,
    dataObject,
    folders,
    onClose,
    onMove,
    onCreateFolder
}) => {
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const firstFocusableRef = useRef<HTMLButtonElement | null>(null);
    const { t } = useTranslate();
    const [selection, setSelection] = useState<number | 'unassign' | 'create-new' | null>(null);
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [folderNameError, setFolderNameError] = useState<string | null>(null);
    const [isCreatingFolderAsync, setIsCreatingFolderAsync] = useState(false);

    const currentFolderId = dataObject?.folderId;
    const currentFolder = folders.find(f => f.id === currentFolderId);
    const candidateFolders = folders.filter(f => f.id !== currentFolderId);
    const showUnassign = true; // Always show "Main Screen" option

    useEffect(() => {
        if (isOpen) {
            setSelection(null); // reset each open
            setIsCreatingFolder(false);
            setNewFolderName('');
            setFolderNameError(null);
            setIsCreatingFolderAsync(false);
        }
    }, [isOpen]);

    // Handle escape key for modal close
    useEffect(() => {
        if (!isOpen) return

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, onClose]);

    if (!isOpen || !dataObject) return null;

    const canConfirm = selection !== null && (
        (selection === 'unassign' && currentFolderId !== undefined) ||
        (typeof selection === 'number' && selection !== currentFolderId) ||
        (selection === 'create-new' && newFolderName.trim() && !folderNameError)
    );

    const handleConfirm = async () => {
        if (!canConfirm) return;

        if (selection === 'unassign') {
            onMove(undefined);
        } else if (typeof selection === 'number') {
            onMove(selection);
        } else if (selection === 'create-new' && onCreateFolder) {
            setIsCreatingFolderAsync(true);
            try {
                const result = await onCreateFolder(newFolderName.trim());
                if (result.valid && result.folder) {
                    onMove(result.folder.id, result.folder);
                } else {
                    const errorMessage = folderValidationMessage(result) || t('Failed to create folder');
                    setFolderNameError(errorMessage);
                    setIsCreatingFolderAsync(false);
                }
            } catch (error) {
                setFolderNameError(t('Failed to create folder. Please try again.'));
                setIsCreatingFolderAsync(false);
            }
        }
    };

    const handleFolderNameChange = (value: string) => {
        setNewFolderName(value);
        if (value.trim()) {
            const existingFolders = folders.map(f => f.name);
            const validationResult = validateFolderName(value, existingFolders);
            const error = folderValidationMessage(validationResult);
            setFolderNameError(error);
        } else {
            setFolderNameError(null);
        }
    };

    const handleCreateNewSelection = () => {
        setSelection('create-new');
        setIsCreatingFolder(true);
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="move-data-object-title"
            onMouseDown={() => { }} // Disabled backdrop click to prevent accidental closes
        >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div
                ref={dialogRef}
                className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 transform transition-all duration-500 flex flex-col max-h-[calc(100vh-2rem)] w-full max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-4xl m-4"
                style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5)'
                }}
            >
                {/* Enhanced Header */}
                <div className="px-3 sm:px-6 py-4 sm:py-5 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                            </div>
                            <div>
                                <h2 id="move-data-object-title" className="text-xl font-bold text-gray-900">
                                    {dataObject.uuid === 'bulk-move' ? t('Move Selected Items') : t('Move Data Object')}
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {t('Select a destination folder for')} <span className="font-medium">
                                        {dataObject.uuid === 'bulk-move' ? dataObject.name : dataObject.name}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <button
                            ref={firstFocusableRef}
                            onClick={onClose}
                            aria-label="Close"
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                </div>

                {/* Enhanced Content */}
                {/* Content Area */}
                <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 sm:py-6 space-y-6">
                    {/* Current Location */}
                    <div className="bg-gradient-to-r from-gray-50 to-red-50 border-2 border-red-100 rounded-xl p-4">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-red-100 rounded-lg">
                                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-700">{t('Current Location:')}</span>
                                <p className="font-bold text-gray-900 text-lg">{currentFolder ? currentFolder.name : t('Main Screen')}</p>
                            </div>
                        </div>
                    </div>

                    {(candidateFolders.length === 0 && !showUnassign) && (
                        <div className="text-center py-8">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <p className="text-gray-500 text-sm">{t('No alternative destinations available')}</p>
                        </div>
                    )}

                    {/* Destination Folders */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                            <span>{t('Choose Destination')}</span>
                        </h3>

                        <fieldset className="space-y-3 max-h-96 overflow-y-auto pr-2" aria-label="Destination Folder">
                            <legend className="sr-only">Choose destination folder</legend>

                            {/* Create New Folder Option - Moved to Top */}
                            {onCreateFolder && (
                                <label className="flex items-center gap-4 p-4 border-2 border-green-200 rounded-xl cursor-pointer hover:bg-green-50 hover:border-green-400 transition-all duration-200 bg-green-25">
                                    <input
                                        type="radio"
                                        name="move-destination"
                                        value="create-new"
                                        onChange={handleCreateNewSelection}
                                        className="w-5 h-5 text-green-600 focus:ring-green-500"
                                    />
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                        </div>
                                        <span className="text-base font-medium text-green-700">{t('Create New Folder')}</span>
                                    </div>
                                </label>
                            )}

                            {/* Existing Folders */}
                            {candidateFolders.map(folder => (
                                <label key={folder.id} className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:bg-red-50 hover:border-red-300 transition-all duration-200">
                                    <input
                                        type="radio"
                                        name="move-destination"
                                        value={folder.id}
                                        onChange={() => setSelection(folder.id)}
                                        className="w-5 h-5 text-red-600 focus:ring-red-500"
                                    />
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-red-100 rounded-lg">
                                            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                            </svg>
                                        </div>
                                        <span className="text-base font-medium text-gray-800">{folder.name}</span>
                                    </div>
                                </label>
                            ))}
                            {/* Main Screen Option */}
                            {showUnassign && (
                                <label className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:bg-orange-50 hover:border-orange-300 transition-all duration-200">
                                    <input
                                        type="radio"
                                        name="move-destination"
                                        value="unassign"
                                        onChange={() => setSelection('unassign')}
                                        className="w-5 h-5 text-orange-600 focus:ring-orange-500"
                                    />
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-orange-100 rounded-lg">
                                            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                                            </svg>
                                        </div>
                                        <span className="text-base font-medium text-gray-800">{t('Main Screen (Unassigned)')}</span>
                                    </div>
                                </label>
                            )}
                        </fieldset>
                    </div>

                    {/* Folder Creation Form */}
                    {isCreatingFolder && selection === 'create-new' && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 space-y-4">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                </div>
                                <label htmlFor="new-folder-name" className="block text-lg font-semibold text-green-800">
                                    {t('New Folder Name')}
                                </label>
                            </div>
                            <input
                                id="new-folder-name"
                                type="text"
                                value={newFolderName}
                                onChange={(e) => handleFolderNameChange(e.target.value)}
                                placeholder={t('Enter folder name...')}
                                className={`w-full px-4 py-3 border-2 rounded-xl text-base focus:outline-none focus:ring-2 ${folderNameError
                                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                        : 'border-green-300 focus:ring-green-500 focus:border-green-500'
                                    } bg-white/80 backdrop-blur-sm`}
                                style={{
                                    fontSize: '16px',
                                    background: 'rgba(255, 255, 255, 0.9)'
                                }}
                                aria-describedby={folderNameError ? 'folder-name-error' : undefined}
                                disabled={isCreatingFolderAsync}
                            />
                            {folderNameError && (
                                <p id="folder-name-error" className="text-sm text-red-600 flex items-center space-x-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{folderNameError}</span>
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Enhanced Footer */}
                <div className="flex-shrink-0 px-3 sm:px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
                    <div className="flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isCreatingFolderAsync}
                            className="px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200 font-medium disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={!canConfirm || isCreatingFolderAsync}
                            onClick={handleConfirm}
                            className="px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200 font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isCreatingFolderAsync ? (
                                <span className="flex items-center gap-2">
                                    <div className="loader-red loader-sm"></div>
                                    {t('Creating...')}
                                </span>
                            ) : selection === 'create-new' ? (
                                t('Create & Move')
                            ) : (
                                t('Move')
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default MoveDataObjectModal;
